import React, { forwardRef, useEffect, useState } from 'react';
import { getConfig } from './config';
import { ICON_HASH } from './iconHash';
import { IIconFontProps } from './types';

const iconCache = new Map<string, string>();
const pendingFetches = new Map<string, Promise<string>>();

const IconFont = forwardRef<HTMLSpanElement, IIconFontProps>((props, ref) => {
    const { name, className = '', color, fontSize, style = {}, onClick, ...restProps } = props;

    const [svgContent, setSvgContent] = useState<string | null>(null);
    const [error, setError] = useState(false);

    const { isIntl } = getConfig();

    useEffect(() => {
        if (!name) return;

        let isMounted = true;
        const isDev = typeof process !== 'undefined' && process.env?.UMI_APP_ENV === 'development'; // 兼容SSR环境
        const cdnPrefix = isIntl ? 'https://x-design-static-release.stariicloud.com' : 'https://pc.meitudata.com';

        const nameOriginal = name.charAt(0).toLowerCase() + name.slice(1);
        const url = isDev
            ? `/x-design-icons/icons/${nameOriginal}.svg`
            : `${cdnPrefix}/x-design-icons/${ICON_HASH}/${nameOriginal}.svg`;

        const loadSVG = async () => {
            if (iconCache.has(name)) {
                setSvgContent(iconCache.get(name)!);
                return;
            }

            if (pendingFetches.has(name)) {
                const content = await pendingFetches.get(name)!;
                if (isMounted) setSvgContent(content);
                return;
            }

            const fetchPromise = fetch(url)
                .then(async (res) => {
                    if (!res.ok) throw new Error(`Failed to load ${name}`);

                    let svgText = await res.text();

                    // 用 DOMParser 解析 SVG 内容为 DOM
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(svgText, 'image/svg+xml');
                    const svg = doc.querySelector('svg');
                    if (!svg) throw new Error(`Invalid SVG content for icon [${name}]`);

                    // 设置 width/height 为 1em
                    svg.setAttribute('width', '1em');
                    svg.setAttribute('height', '1em');

                    // 设置 fill="currentColor"（排除 fill="none"）
                    svg.querySelectorAll('[fill]').forEach((el) => {
                        const fill = el.getAttribute('fill');
                        if (fill && fill.toLowerCase() !== 'none') {
                            el.setAttribute('fill', 'currentColor');
                        }
                    });

                    // 移除 <defs> 节点
                    svg.querySelectorAll('defs').forEach((defs) => defs.remove());

                    // 移除 clip-path、mask、filter 属性
                    ['clip-path', 'mask', 'filter'].forEach((attr) => {
                        svg.querySelectorAll(`[${attr}]`).forEach((el) => {
                            el.removeAttribute(attr);
                        });
                    });

                    return svg.outerHTML;
                })
                .then((svg) => {
                    iconCache.set(name, svg);
                    return svg;
                })
                .catch((err) => {
                    console.error(`Icon load error [${name}]:`, err);
                    setError(true);
                    return '';
                });

            pendingFetches.set(name, fetchPromise);
            const svg = await fetchPromise;
            if (isMounted) setSvgContent(svg);
        };

        loadSVG();

        return () => {
            isMounted = false;
        };
    }, [name]);

    const size = style.fontSize || fontSize || 12;

    return (
        <span
            className={`iconfont icon-${name} ${className}`}
            alias-name={name}
            style={{
                width: size,
                height: size,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color,
                fontSize: size,
                ...style,
            }}
            dangerouslySetInnerHTML={svgContent && !error ? { __html: svgContent } : undefined}
            ref={ref}
            {...restProps}
            onClick={onClick}
        ></span>
    );
});

export default React.memo(IconFont);
